/**
 * AnnotationRenderer3D — Draws route tubes, bracket lines, zone boxes,
 * highlight rings, and label sprites in the 3D scene.
 *
 * Uses the same annotation type dispatch as the 2D FootballScene but
 * renders with Three.js primitives.
 */

import * as THREE from 'three';

function parseColor(raw, fallback) {
  if (!raw) return fallback;
  const s = String(raw).replace(/^0x/i, '').replace(/^#/, '');
  const parsed = parseInt(s, 16);
  return isNaN(parsed) ? fallback : parsed;
}

function makeLabel(text, color, position) {
  const canvas = document.createElement('canvas');
  const size = 36;
  canvas.width = size * Math.max(text.length, 2) * 0.7 + 20;
  canvas.height = size + 12;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(0,0,0,0.65)';
  ctx.beginPath();
  ctx.roundRect(0, 0, canvas.width, canvas.height, 4);
  ctx.fill();
  ctx.font = `bold ${size}px Arial`;
  ctx.fillStyle = color || '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  const aspect = canvas.width / canvas.height;
  const h = 1.0;
  sprite.scale.set(h * aspect, h, 1);
  sprite.position.copy(position);
  sprite.position.y += 3.5;
  return sprite;
}

export default class AnnotationRenderer3D {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'annotations';
    scene.add(this.group);
    this.playerGroups = {};
  }

  setPlayerGroups(pg) {
    this.playerGroups = pg;
  }

  clear() {
    // Restore actor opacity in case a previous frame applied a spotlight
    // (which dims non-target players). This mirrors Phaser's behaviour
    // where spotlight is a per-event overlay, not a persistent state.
    this._restoreAllActorOpacity();
    while (this.group.children.length > 0) {
      const child = this.group.children[0];
      child.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (obj.material.map) obj.material.map.dispose();
          obj.material.dispose();
        }
      });
      this.group.remove(child);
    }
  }

  apply(annotations) {
    this.clear();
    if (!annotations || !Array.isArray(annotations)) return;
    annotations.forEach((ann) => {
      switch (ann.type) {
        case 'route': this._route(ann); break;
        case 'bracket': this._bracket(ann); break;
        case 'zone': this._zone(ann); break;
        case 'highlight': this._highlight(ann); break;
        case 'spotlight': this._spotlight(ann); break;
        case 'matchup': this._matchup(ann); break;
        case 'label': this._label(ann); break;
        // Sprint 2 — coach chalk-talk strokes.
        case 'telestration': this._telestration(ann); break;
        default: break;
      }
    });
  }

  /**
   * Apply a list of TelestrationStroke directly. Coaches author them
   * separately from `annotations` so the runtime calls this in addition
   * to (not instead of) `apply()`. Strokes layer on top of route/zone
   * annotations and clear together at the next event boundary.
   */
  applyTelestration(strokes) {
    if (!strokes || !Array.isArray(strokes)) return;
    strokes.forEach((s) => this._telestration({ ...s, type: 'telestration' }));
  }

  _getPos(actorId) {
    const g = this.playerGroups[actorId];
    return g ? g.position.clone() : null;
  }

  _route(ann) {
    const actors = ann.actors || [];
    if (actors.length < 1) return;
    const start = this._getPos(actors[0]);
    if (!start) return;
    const target = ann.position;
    if (!target || (!target.x && !target.y)) return;
    const end = new THREE.Vector3(target.x, 0, target.y);
    const color = parseColor(ann.style?.color, 0x00bfff);

    const mid = new THREE.Vector3().lerpVectors(start, end, 0.5);
    mid.y = 4;

    const curve = new THREE.QuadraticBezierCurve3(
      new THREE.Vector3(start.x, 0.5, start.z),
      mid,
      new THREE.Vector3(end.x, 0.5, end.z)
    );

    const tubeGeo = new THREE.TubeGeometry(curve, 24, 0.12, 6, false);
    const tubeMat = new THREE.MeshStandardMaterial({
      color, transparent: true, opacity: 0.85, roughness: 0.6,
    });
    this.group.add(new THREE.Mesh(tubeGeo, tubeMat));

    const coneGeo = new THREE.ConeGeometry(0.35, 0.8, 6);
    const coneMat = new THREE.MeshStandardMaterial({ color });
    const cone = new THREE.Mesh(coneGeo, coneMat);
    const tangent = curve.getTangentAt(1).normalize();
    cone.position.set(end.x, 0.5, end.z);
    cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), tangent);
    this.group.add(cone);

    if (ann.label) {
      this.group.add(makeLabel(ann.label, '#ffffff', end));
    }
  }

  _bracket(ann) {
    const actors = ann.actors || [];
    if (actors.length < 2) return;
    const p1 = this._getPos(actors[0]);
    const p2 = this._getPos(actors[1]);
    if (!p1 || !p2) return;
    const color = parseColor(ann.style?.color, 0xff6b6b);

    const targetPos = ann.target ? this._getPos(ann.target) : null;

    const positions = [];
    if (targetPos) {
      positions.push(p1.x, 1, p1.z, targetPos.x, 1, targetPos.z);
      positions.push(p2.x, 1, p2.z, targetPos.x, 1, targetPos.z);
    } else {
      positions.push(p1.x, 1, p1.z, p2.x, 1, p2.z);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.7 });
    this.group.add(new THREE.LineSegments(geo, mat));

    if (ann.label) {
      const lp = targetPos || new THREE.Vector3().lerpVectors(p1, p2, 0.5);
      this.group.add(makeLabel(ann.label, `#${color.toString(16).padStart(6, '0')}`, lp));
    }
  }

  _zone(ann) {
    const pos = ann.position || {};
    const x = pos.x || 50;
    const z = pos.y || 26;
    const w = (ann.style?.width || 20);
    const h = (ann.style?.height || 15);
    const color = parseColor(ann.style?.color, 0x3b82f6);
    const alpha = ann.style?.alpha || 0.15;

    const geo = new THREE.BoxGeometry(w, 0.3, h);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: alpha, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, 0.15, z);
    this.group.add(mesh);

    const edgeGeo = new THREE.EdgesGeometry(geo);
    const edgeMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.4 });
    const edges = new THREE.LineSegments(edgeGeo, edgeMat);
    edges.position.copy(mesh.position);
    this.group.add(edges);
  }

  _highlight(ann) {
    const actors = ann.actors || [];
    const color = parseColor(ann.style?.color, 0xffeb3b);
    actors.forEach((actorId) => {
      const pos = this._getPos(actorId);
      if (!pos) return;
      const ringGeo = new THREE.RingGeometry(1.0, 1.3, 16);
      const ringMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(pos.x, 0.05, pos.z);
      ring.userData._pulse = true;
      this.group.add(ring);
    });
  }

  /**
   * Spotlight annotation — Phaser parity (FootballScene.drawSpotlight).
   *
   * Highlights the listed actors with a strong gold pulsing ring and
   * dims every other actor's model to ``ann.dim_opacity`` (default 0.3).
   * Used by the DefensiveFrontQuiz scene type to draw the learner's eye
   * to the defender being asked about ("which technique is this?")
   * without removing the rest of the alignment from view.
   *
   * Restoration: ``clear()`` calls ``_restoreAllActorOpacity()`` before
   * applying the next event's annotations, so spotlight is per-frame
   * rather than a sticky state. The annotation is mounted on the
   * annotations group; the dimming is applied to the shared
   * playerGroups (which we do not own), so we record which groups were
   * touched and reset them rather than mutating every actor on the
   * field on every frame.
   */
  _spotlight(ann) {
    const highlighted = new Set(ann.actors || []);
    const dimAlpha = (ann.dim_opacity !== undefined && ann.dim_opacity !== null)
      ? ann.dim_opacity
      : 0.3;
    const color = parseColor(ann.style?.color, 0xffd700);

    this._dimmedGroups = this._dimmedGroups || new Set();

    Object.entries(this.playerGroups || {}).forEach(([actorId, group]) => {
      if (!group) return;
      if (highlighted.has(actorId)) {
        this._setGroupOpacity(group, 1.0, false);
      } else {
        this._setGroupOpacity(group, dimAlpha, true);
        this._dimmedGroups.add(actorId);
      }
    });

    highlighted.forEach((actorId) => {
      const pos = this._getPos(actorId);
      if (!pos) return;
      const ringGeo = new THREE.RingGeometry(1.4, 1.75, 24);
      const ringMat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.85,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(pos.x, 0.06, pos.z);
      ring.userData._pulse = true;
      this.group.add(ring);

      if (ann.label) {
        this.group.add(makeLabel(ann.label, '#ffd700', pos));
      }
    });
  }

  /**
   * Walks a player group's meshes and adjusts material opacity. We
   * stash the original ``transparent`` and ``opacity`` on userData on
   * first dim so we can restore exactly what the model shipped with —
   * some PlayerModel materials are deliberately transparent (helmet
   * visor, shadow plane), and we don't want to opaque them by accident
   * during a restore.
   */
  _setGroupOpacity(group, opacity, transparent) {
    if (!group) return;
    group.traverse((obj) => {
      if (!obj.material) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if (m.userData._origOpacity === undefined) {
          m.userData._origOpacity = m.opacity;
          m.userData._origTransparent = m.transparent;
        }
        m.transparent = transparent || m.userData._origTransparent;
        m.opacity = opacity;
        m.needsUpdate = true;
      });
    });
  }

  _restoreAllActorOpacity() {
    if (!this._dimmedGroups || this._dimmedGroups.size === 0) return;
    this._dimmedGroups.forEach((actorId) => {
      const group = (this.playerGroups || {})[actorId];
      if (!group) return;
      group.traverse((obj) => {
        if (!obj.material) return;
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if (m.userData._origOpacity !== undefined) {
            m.opacity = m.userData._origOpacity;
            m.transparent = m.userData._origTransparent;
            m.needsUpdate = true;
          }
        });
      });
    });
    this._dimmedGroups.clear();
  }

  _matchup(ann) {
    const actors = ann.actors || [];
    if (actors.length < 1) return;
    const p1 = this._getPos(actors[0]);
    const p2 = ann.target ? this._getPos(ann.target) : null;
    if (!p1 || !p2) return;

    const positions = [p1.x, 1.5, p1.z, p2.x, 1.5, p2.z];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const mat = new THREE.LineDashedMaterial({ color: 0xffffff, dashSize: 0.5, gapSize: 0.3, transparent: true, opacity: 0.6 });
    const line = new THREE.LineSegments(geo, mat);
    line.computeLineDistances();
    this.group.add(line);

    if (ann.label) {
      const mid = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
      this.group.add(makeLabel(ann.label, '#ffffff', mid));
    }
  }

  _label(ann) {
    const pos = ann.position || {};
    const p = new THREE.Vector3(pos.x || 50, 0, pos.y || 26);
    const text = ann.text || ann.label || '';
    if (text) this.group.add(makeLabel(text, '#ffffff', p));
  }

  /**
   * Sprint 2 — render a coach's chalk-talk stroke as world-space
   * geometry so it survives camera changes (top-down → QB → sideline).
   *
   * Stroke shapes:
   *   line / arrow  — points = [start, end]
   *   freehand      — points = [p0, p1, … pn]   (smoothed by a Catmull spline)
   *   circle        — points = [center, radius_point]
   *   rect          — points = [top_left, bottom_right]
   *   text          — points = [position]; rendered via makeLabel
   *
   * Coordinate space matches the rest of the runtime — field yards
   * (x = sideline-to-sideline, y = goalline-to-goalline). Strokes
   * float at y = 0.18 in world coords so they render on top of the
   * field paint without z-fighting.
   */
  _telestration(stroke) {
    const STROKE_Y = 0.18;
    const points = Array.isArray(stroke.points) ? stroke.points : [];
    if (points.length === 0 && (stroke.type !== 'text' || !stroke.text)) return;

    const color = parseColor(stroke.color, 0xffd700);
    const width = Math.max(0.05, stroke.stroke_width || 0.4);

    const toV3 = (p) => new THREE.Vector3(Number(p.x) || 0, STROKE_Y, Number(p.y) || 0);

    switch (stroke.type) {
      case 'line':
      case 'arrow': {
        if (points.length < 2) return;
        const a = toV3(points[0]);
        const b = toV3(points[points.length - 1]);
        this._addThickLine([a, b], color, width, !!stroke.dashed);
        if (stroke.type === 'arrow') {
          this._addArrowhead(a, b, color, width);
        }
        if (stroke.label) {
          this.group.add(makeLabel(stroke.label, `#${color.toString(16).padStart(6, '0')}`,
            new THREE.Vector3((a.x + b.x) / 2, STROKE_Y, (a.z + b.z) / 2)));
        }
        return;
      }
      case 'freehand': {
        if (points.length < 2) return;
        const v3s = points.map(toV3);
        // Smooth with a centripetal Catmull-Rom curve so freehand
        // sketches don't render as a polyline of jagged segments.
        const curve = new THREE.CatmullRomCurve3(v3s, false, 'centripetal');
        const samples = curve.getPoints(Math.max(v3s.length * 4, 24));
        this._addThickLine(samples, color, width, !!stroke.dashed);
        return;
      }
      case 'circle': {
        if (points.length < 2) return;
        const center = toV3(points[0]);
        const edge = toV3(points[1]);
        const radius = Math.max(0.5, Math.hypot(edge.x - center.x, edge.z - center.z));
        const segments = 48;
        const pts = [];
        for (let i = 0; i <= segments; i += 1) {
          const ang = (i / segments) * Math.PI * 2;
          pts.push(new THREE.Vector3(
            center.x + Math.cos(ang) * radius,
            STROKE_Y,
            center.z + Math.sin(ang) * radius,
          ));
        }
        this._addThickLine(pts, color, width, !!stroke.dashed);
        if (stroke.fill) {
          const fillColor = parseColor(stroke.fill, color);
          const ringGeo = new THREE.CircleGeometry(radius, segments);
          const ringMat = new THREE.MeshBasicMaterial({
            color: fillColor, transparent: true, opacity: 0.18, side: THREE.DoubleSide,
          });
          const ring = new THREE.Mesh(ringGeo, ringMat);
          ring.rotation.x = -Math.PI / 2;
          ring.position.set(center.x, STROKE_Y - 0.02, center.z);
          this.group.add(ring);
        }
        return;
      }
      case 'rect': {
        if (points.length < 2) return;
        const p1 = toV3(points[0]);
        const p2 = toV3(points[1]);
        const corners = [
          p1.clone(),
          new THREE.Vector3(p2.x, STROKE_Y, p1.z),
          p2.clone(),
          new THREE.Vector3(p1.x, STROKE_Y, p2.z),
          p1.clone(),
        ];
        this._addThickLine(corners, color, width, !!stroke.dashed);
        return;
      }
      case 'text': {
        const pos = points[0] || stroke.position || { x: 50, y: 26 };
        if (stroke.text) {
          this.group.add(makeLabel(
            stroke.text,
            `#${color.toString(16).padStart(6, '0')}`,
            new THREE.Vector3(Number(pos.x) || 50, STROKE_Y, Number(pos.y) || 26),
          ));
        }
        return;
      }
      default:
        return;
    }
  }

  /** Add a thick polyline by tube-extruding the curve. Width in yards. */
  _addThickLine(points, color, width, dashed) {
    if (!points || points.length < 2) return;
    if (dashed) {
      // Dashed strokes use a regular Line with dash material — cheaper,
      // and tube dashing isn't supported by Three.js out of the box.
      const geo = new THREE.BufferGeometry().setFromPoints(points);
      const mat = new THREE.LineDashedMaterial({
        color, dashSize: 1.0, gapSize: 0.6, linewidth: 2,
      });
      const line = new THREE.Line(geo, mat);
      line.computeLineDistances();
      this.group.add(line);
      return;
    }
    const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal');
    const tubeGeo = new THREE.TubeGeometry(curve, Math.max(points.length * 3, 24), width / 2, 6, false);
    const tubeMat = new THREE.MeshStandardMaterial({
      color,
      transparent: true,
      opacity: 0.95,
      roughness: 0.4,
      metalness: 0.1,
    });
    this.group.add(new THREE.Mesh(tubeGeo, tubeMat));
  }

  _addArrowhead(start, end, color, width) {
    const dx = end.x - start.x;
    const dz = end.z - start.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.1) return;
    const dir = new THREE.Vector3(dx / len, 0, dz / len);
    const headLen = Math.max(width * 4, 1.0);
    const headRad = Math.max(width * 2.4, 0.6);
    const coneGeo = new THREE.ConeGeometry(headRad, headLen, 10);
    const coneMat = new THREE.MeshStandardMaterial({ color, transparent: true, opacity: 0.95 });
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.set(end.x, end.y, end.z);
    cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    this.group.add(cone);
  }

  update(dt) {
    const t = Date.now() * 0.003;
    this.group.traverse((obj) => {
      if (obj.userData?._pulse && obj.material) {
        obj.material.opacity = 0.2 + 0.25 * Math.sin(t);
      }
    });
  }

  dispose() {
    this.clear();
    this.scene.remove(this.group);
  }
}
